import { inject, injectable } from 'inversify';

import { BOOKINGKIT_API_FACTORY, SUPPLIER_STATE_SERVICE } from '@/bootstrap/ServiceProviders';
import { BookingKitApiFactoryInterface } from '@/models/Factories/BookingKitApiFactoryInterface';
import { UseSupplierInterface } from '@/state/supplier';
import { PaginatedAndCancellableRequest } from '@/services/VoucherService/types';
import {
    CreateWidgetRequest,
    ListWidgetResponse,
    UpdateWidgetRequest,
} from '@bookingkit-private/api-v4';
import {
    fromApiThemeToThemeType,
    fromApiWidgetToWidgetType,
} from '@/services/WidgetService/Reducers';
import { ThemeType, WidgetType } from '@/services/WidgetService/types';

@injectable()
export class WidgetService {
    private connection: ReturnType<BookingKitApiFactoryInterface['getConnection']>;

    private supplierId: string;

    constructor(
        @inject(BOOKINGKIT_API_FACTORY) factory: BookingKitApiFactoryInterface,
        @inject(SUPPLIER_STATE_SERVICE) useSupplierState: () => UseSupplierInterface,
    ) {
        const { supplier } = useSupplierState();
        this.supplierId = supplier.value!.id;
        this.connection = factory.getConnection();
        this.getAllWidgets = this.getAllWidgets.bind(this);
    }

    async getAllWidgets(params?: PaginatedAndCancellableRequest): Promise<{widgets: WidgetType[], nextPageToken: string|undefined}> {
        const { supplierId } = this;
        const parameters = params ?? {};
        const {
            pageSize, nextPageToken, signal,
        } = parameters;
        const { data } = await this.connection.listWidgets({vendorId:supplierId,
            pageSize,
            asc:1 as unknown as boolean,
            pageToken:nextPageToken, sortBy:'title'},
        { signal }) as unknown as ListWidgetResponse;
        const { data: widgets, metadata } = data as unknown as Required<ListWidgetResponse>;
        return {
            widgets: widgets?.map(fromApiWidgetToWidgetType) ?? [],
            nextPageToken: metadata?.["page-token"],
        };
    }

    async getWidget(widgetId: string): Promise<WidgetType> {
        const { data } = await this.connection.getWidget({widgetId});
        return fromApiWidgetToWidgetType(data);
    }

    async getSupplierThemes(): Promise<ThemeType[]> {
        const { data } = await this.connection.listThemes({vendorId:this.supplierId});
        if (!data.data) {
            return [];
        }
        return data.data.map(fromApiThemeToThemeType);
    }

    async createNewWidget({ title, description, config }:{title: string, description?: string, config?: any}): Promise<WidgetType> {
        const { supplierId } = this;
        const createWidgetRequest: CreateWidgetRequest = {
            title,
            description,
            config,
        };
        const { data: widget } = await this.connection
            .createWidget({vendorId:supplierId, createWidgetRequest:createWidgetRequest});
        return fromApiWidgetToWidgetType(widget);
    }

    async updateWidget({
        id, title, description, config,
    }:{id: string, title?: string, description?: string, config: any}): Promise<void> {
        const { supplierId } = this;
        const updateWidgetRequest: UpdateWidgetRequest = {
            title,
            description,
            config,
        };
        await this.connection.updateWidget({vendorId: supplierId, widgetId: id, updateWidgetRequest});
    }

    async deleteWidget({ id }:{id: string}): Promise<void> {
        const { supplierId } = this;
        await this.connection.deleteWidget({vendorId:supplierId, widgetId:id});
    }
}

export default { WidgetService };
